import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, API } from "@/lib/api";
import { CheckCircle2, Copy, Loader2, ArrowRight, Users, Download, FileText } from "lucide-react";

/*  /packs/success — post-Stripe handoff. Polls the order until the webhook
    (or lazy reconciliation inside the endpoint) flips status → paid, then
    reveals the access code the buyer needs to launch their crew lobby.
*/

const PackSuccess = () => {
  const [sp] = useSearchParams();
  const orderId  = sp.get("order_id") || "";
  const sessionId = sp.get("session_id") || "";
  const [order, setOrder] = useState(null);
  const [preview, setPreview] = useState(null);
  const [copied, setCopied] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!orderId) { setErr("Missing order id."); return; }
    let cancelled = false;
    let attempts = 0;
    const poll = async () => {
      attempts++;
      try {
        const url = `/packs/order/${orderId}` + (sessionId ? `?session_id=${sessionId}` : "");
        const { data } = await api.get(url);
        if (cancelled) return;
        setOrder(data);
        if (data.status !== "paid" && attempts < 15) {
          setTimeout(poll, 1500);
        }
      } catch {
        if (!cancelled) setErr("Couldn't locate your order. If payment went through you'll still get the email.");
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [orderId, sessionId]);

  // Fetch the case preview card as soon as we know which pack this is
  useEffect(() => {
    if (!order?.pack_id) return;
    api.get(`/packs/kit-preview/${order.pack_id}`)
      .then((r) => setPreview(r.data || null))
      .catch(() => setPreview(null));
  }, [order?.pack_id]);

  const copyCode = async () => {
    if (!order?.access_code) return;
    try {
      await navigator.clipboard.writeText(order.access_code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {}
  };

  return (
    <div className="min-h-screen bg-[#050403] text-[#f4f0ec] flex items-center justify-center px-5 pb-24"
      data-testid="pack-success-page">
      <div className="max-w-xl w-full">
        {err && (
          <div className="card-noir p-6 text-center text-[#f87171]" data-testid="pack-success-error">
            {err}
          </div>
        )}

        {!err && !order && (
          <div className="card-noir p-10 text-center text-[#a39797]">
            <Loader2 size={20} className="animate-spin inline mr-2" /> Confirming your payment…
          </div>
        )}

        {order && order.status !== "paid" && (
          <div className="card-noir p-10 text-center" data-testid="pack-success-pending">
            <Loader2 size={20} className="animate-spin inline mr-2 text-[#d4af37]" />
            <div className="text-[10px] tracking-[0.3em] text-[#d4af37] mt-2">AWAITING STRIPE CONFIRMATION</div>
            <div className="text-sm text-[#a39797] mt-3">
              Hang tight. Your access code is being minted. This usually takes a few seconds.
            </div>
          </div>
        )}

        {order && order.status === "paid" && (
          <div data-testid="pack-success-paid">
            <div className="card-noir p-8 text-center bg-black/70">
              <CheckCircle2 size={48} className="text-[#4ade80] mx-auto mb-3" />
              <div className="text-[10px] tracking-[0.3em] text-[#4ade80] mb-2">PAYMENT CONFIRMED</div>
              <div className="font-display text-4xl text-[#f4f0ec]">
                Your {order.pack_label} is live.
              </div>
              <div className="text-xs text-[#a39797] mt-2 flex items-center justify-center gap-1">
                <Users size={10} /> Up to {order.max_players} player{order.max_players > 1 ? "s" : ""}
                &nbsp;·&nbsp;
                {order.cases_included} case{order.cases_included > 1 ? "s" : ""}
              </div>
            </div>

            <div className="card-noir p-6 mt-5 text-center border-[#d4af37]"
              data-testid="pack-success-code-block">
              <div className="text-[10px] tracking-[0.3em] text-[#d4af37] mb-2">ACCESS CODE</div>
              <div className="font-mono text-3xl sm:text-4xl text-[#d4af37] tracking-[0.3em] mb-3"
                data-testid="pack-success-code">
                {order.access_code}
              </div>
              <button onClick={copyCode}
                className="btn-ghost text-xs" data-testid="pack-success-copy">
                {copied
                  ? <><CheckCircle2 size={12} className="text-green-400" /> Copied</>
                  : <><Copy size={12} /> Copy code</>}
              </button>
              <div className="text-[10px] text-[#5a5050] mt-3 leading-relaxed">
                Also sent to your email. Don't lose it — your crew needs it to join.
              </div>
            </div>

            <div className="text-center mt-6">
              <Link to="/" className="btn-primary" data-testid="pack-success-home">
                Enter the arena <ArrowRight size={14} />
              </Link>
            </div>

            {/* Case preview + PDF download — the actual kit the buyer paid for */}
            {preview?.available && (
              <div className="card-noir p-6 mt-6" data-testid="pack-success-kit-download">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-[#8b0000]/20 border border-[#8b0000]/40 flex items-center justify-center flex-shrink-0">
                    <FileText size={20} className="text-[#d4af37]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[10px] tracking-[0.3em] text-[#d4af37] mb-1">
                      CASE #{preview.case_number} · YOUR KIT
                    </div>
                    <div className="font-display text-2xl text-[#f4f0ec] leading-tight mb-1">
                      {preview.title}
                    </div>
                    <div className="text-xs text-[#a39797] leading-relaxed mb-3">
                      {preview.tagline}
                    </div>
                    <div className="text-[10px] text-[#5a5050] mb-4 flex items-center gap-2">
                      <Users size={10} /> Sized for {preview.player_count} players · Printable PDF
                    </div>
                    <a
                      href={`${API}/packs/kit/${order.order_id}.pdf`}
                      target="_blank"
                      rel="noopener"
                      className="btn-primary text-xs"
                      data-testid="pack-success-kit-download-btn"
                    >
                      <Download size={13} /> Download the full kit (PDF)
                    </a>
                    <div className="text-[10px] text-[#5a5050] mt-3 leading-relaxed">
                      Role cards, host script, two clue sets, and the reveal — all in one printable file. Re-download anytime from this page.
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Cross-sell — upsell to the next tier before buyer leaves */}
            {order.cross_sell && (
              <div className="card-noir p-5 mt-8 bg-gradient-to-br from-[#d4af37]/10 to-transparent border-[#d4af37]/40"
                data-testid="pack-success-crosssell">
                <div className="text-[10px] tracking-[0.3em] text-[#d4af37] mb-2">
                  BEFORE YOU GO · UPGRADE OFFER
                </div>
                <div className="font-display text-xl text-[#f4f0ec] leading-tight">
                  You've got {order.pack_label}. Add just <span className="text-[#d4af37]">{order.cross_sell.upgrade_delta_label}</span> and jump to {order.cross_sell.target_label}.
                </div>
                <div className="text-xs text-[#c7bebe] mt-2 leading-relaxed">
                  {order.cross_sell.pitch}.
                  {order.cross_sell.cases_delta > 0 && (
                    <> You unlock <span className="text-[#d4af37]">{order.cross_sell.cases_delta} more case{order.cross_sell.cases_delta > 1 ? "s" : ""}</span>.</>
                  )}
                  {order.cross_sell.players_delta > 0 && (
                    <> Scale to <span className="text-[#d4af37]">{order.cross_sell.players_delta} more players</span>.</>
                  )}
                  {order.cross_sell.bf_active && (
                    <> <span className="text-[#8b0000] font-bold">Black Friday discount already baked in.</span></>
                  )}
                </div>
                <Link to="/packs" className="btn-ghost text-xs mt-4"
                  data-testid="pack-success-crosssell-cta">
                  See {order.cross_sell.target_label} ({order.cross_sell.target_price_label}) <ArrowRight size={12} />
                </Link>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default PackSuccess;
